/**
 * One assignment, staff view — `/team-leader/assignments/$assignmentId`.
 *
 * Four tabs in the order the work actually happens: build the BRIEF (instructions + media), watch the
 * SUBMISSIONS come in, chase whoever has NOT DELIVERED, read the STATS. Publishing is a header action
 * rather than a tab, because it is the one irreversible-feeling step and it should be visible from all
 * of them.
 *
 * Submissions and Not delivered are complements, and both are needed: a grid of submissions can only
 * show students who acted, so without the second tab the students who did nothing are invisible on the
 * page that exists to track them.
 *
 * A draft is deliberately usable: media can be attached before publishing, so the brief is complete
 * the moment students first see it.
 */
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { TableSkeleton } from "@/components/loading";
import { useAuth } from "@/hooks/use-auth";
import {
  useAssignment,
  useAssignmentNonDelivery,
  useAssignmentStats,
  useAssignmentSubmissions,
  useDeleteAssignment,
  useSetAssignmentPublished,
} from "@/hooks/use-assignments";
import { AssignmentFormDialog } from "@/components/assignments/assignment-form-dialog";
import { ShareAssignmentDialog } from "@/components/assignments/share-assignment-dialog";
import { AssignmentAudienceCard } from "@/components/assignments/assignment-audience-card";
import { BriefMediaManager } from "@/components/assignments/brief-media-manager";
import { BriefMediaList } from "@/components/assignments/brief-media-list";
import { SubmissionReviewPanel } from "@/components/assignments/submission-review-panel";
import { NotDeliveredPanel } from "@/components/assignments/not-delivered-panel";
import { SubmissionStatusBadge } from "@/components/assignments/submission-status-badge";
import { SubmissionStatus } from "@/lib/api/assignments";
import {
  ArrowLeft,
  ChevronRight,
  ClipboardList,
  Eye,
  EyeOff,
  Loader2,
  Paperclip,
  Pencil,
  Share2,
  Trash2,
  Users,
} from "lucide-react";

export const Route = createFileRoute("/_authenticated/team-leader/assignments_/$assignmentId")({
  component: AssignmentDetailPage,
});

function formatWhen(iso: string | null): string {
  if (!iso) return "—";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
}

function StatTile({
  label,
  value,
  hint,
}: {
  label: string;
  value: string | number;
  hint?: string;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription>{label}</CardDescription>
      </CardHeader>
      <CardContent>
        <p className="text-2xl font-semibold">{value}</p>
        {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      </CardContent>
    </Card>
  );
}

function AssignmentDetailPage() {
  const { assignmentId } = Route.useParams();
  const navigate = useNavigate();

  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [reviewingId, setReviewingId] = useState<string | null>(null);

  // Authoring the brief and grading what comes back are separate permissions server-side
  // (AppRoles.AssignmentAuthoring vs AppRoles.Grading), so a ContentCreator reaches this page for
  // the courses they wrote but is refused every submission read. The Submissions tab is dropped for
  // them rather than left to 403 — Brief and Statistics are theirs.
  const { hasPermission } = useAuth();
  const canReview = hasPermission("grade:submissions");

  const { data: assignment, isLoading, isError, error } = useAssignment(assignmentId);
  const submissions = useAssignmentSubmissions(assignmentId, canReview);
  // Fetched here rather than only inside the tab so the tab itself can carry the count — the whole
  // point of the surface is that a reviewer sees the number without going looking for it. The panel
  // reads the same query key, so opening the tab costs no second request.
  const notDelivered = useAssignmentNonDelivery(assignmentId, canReview);
  const stats = useAssignmentStats(assignmentId);
  const setPublished = useSetAssignmentPublished(assignmentId);
  const remove = useDeleteAssignment();

  if (isLoading) {
    return (
      <div className="space-y-4 p-4 md:p-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (isError || !assignment) {
    return (
      <div className="space-y-4 p-4 md:p-6">
        <Button asChild variant="ghost" size="sm">
          <Link to="/team-leader/assignments">
            <ArrowLeft className="mr-1.5 h-4 w-4" />
            Back to assignments
          </Link>
        </Button>
        <p className="text-sm text-destructive">
          {error instanceof Error ? error.message : "This assignment could not be loaded."}
        </p>
      </div>
    );
  }

  const rows = submissions.data ?? [];

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div>
        <Button asChild variant="ghost" size="sm" className="-ml-2 mb-2">
          <Link to="/team-leader/assignments">
            <ArrowLeft className="mr-1.5 h-4 w-4" />
            Back to assignments
          </Link>
        </Button>

        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-semibold tracking-tight">{assignment.title}</h1>
              {assignment.isPublished ? (
                <Badge
                  variant="outline"
                  className="border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                >
                  Published
                </Badge>
              ) : (
                <Badge variant="secondary">Draft</Badge>
              )}
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              {assignment.courseName}
              {assignment.moduleTitle && ` · ${assignment.moduleTitle}`} ·{" "}
              {assignment.pointsPossible} points
              {assignment.dueDate && ` · due ${formatWhen(assignment.dueDate)}`}
            </p>
          </div>

          {/*
            Gated on the server's own answer (`canManage`), not on a role permission: a team leader
            reviewing their students' work on a course somebody else wrote reaches this page and is
            refused every write on it. Rendering these would be four buttons that 403 when pressed.
          */}
          {assignment.canManage && (
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
                <Pencil className="mr-1.5 h-4 w-4" />
                Edit
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShareOpen(true)}
                // Enabled while unpublished so the dialog can explain why, rather than presenting a dead
                // control with no reason attached.
                title={
                  assignment.isPublished
                    ? "Post this assignment into a chat"
                    : "Publish it first — students cannot open a draft"
                }
              >
                <Share2 className="mr-1.5 h-4 w-4" />
                Share to chat
              </Button>
              <Button
                size="sm"
                variant={assignment.isPublished ? "outline" : "default"}
                disabled={setPublished.isPending}
                onClick={() => setPublished.mutate(!assignment.isPublished)}
              >
                {setPublished.isPending ? (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                ) : assignment.isPublished ? (
                  <EyeOff className="mr-1.5 h-4 w-4" />
                ) : (
                  <Eye className="mr-1.5 h-4 w-4" />
                )}
                {assignment.isPublished ? "Withdraw" : "Publish"}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive"
                onClick={() => setDeleteOpen(true)}
              >
                <Trash2 className="mr-1.5 h-4 w-4" />
                Delete
              </Button>
            </div>
          )}
        </div>

        {!assignment.isPublished && (
          <p className="mt-3 rounded-md border border-dashed bg-muted/20 p-3 text-sm text-muted-foreground">
            Students cannot see or submit to this yet. Attach the brief&apos;s media first, then
            publish — publishing notifies everyone on the course once.
          </p>
        )}
      </div>

      <Tabs defaultValue="brief">
        <TabsList>
          <TabsTrigger value="brief">
            Brief
            {assignment.attachmentCount > 0 && (
              <Badge variant="secondary" className="ml-1.5">
                {assignment.attachmentCount}
              </Badge>
            )}
          </TabsTrigger>
          {canReview && (
            <TabsTrigger value="submissions">
              Submissions
              {assignment.pendingReviewCount > 0 && (
                <Badge variant="secondary" className="ml-1.5">
                  {assignment.pendingReviewCount}
                </Badge>
              )}
            </TabsTrigger>
          )}
          {/*
            The complement of Submissions, and gated on the same permission: the grid there can only
            show students who acted, so this is the only tab that names the ones who did not. Badged with
            the overdue count rather than the whole list — an assignment that is not due yet has nobody
            to chase, and a red count on it would read as a problem.
          */}
          {canReview && (
            <TabsTrigger value="not-delivered">
              Not delivered
              {notDelivered.data && notDelivered.data.students.length > 0 && (
                <Badge
                  variant={notDelivered.data.overdueCount > 0 ? "destructive" : "secondary"}
                  className="ml-1.5"
                >
                  {notDelivered.data.students.length}
                </Badge>
              )}
            </TabsTrigger>
          )}
          <TabsTrigger value="stats">Statistics</TabsTrigger>
        </TabsList>

        {/* ── Brief ────────────────────────────────────────────────────────── */}
        <TabsContent value="brief" className="mt-4 space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Instructions</CardTitle>
            </CardHeader>
            <CardContent>
              {assignment.description ? (
                <p className="whitespace-pre-wrap text-sm">{assignment.description}</p>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No written instructions. Use Edit to add them.
                </p>
              )}
              <div className="mt-3 flex flex-wrap gap-2 text-xs text-muted-foreground">
                <Badge variant="outline">
                  {assignment.allowResubmission
                    ? assignment.maxAttempts
                      ? `Up to ${assignment.maxAttempts} attempts`
                      : "Unlimited attempts"
                    : "Single attempt"}
                </Badge>
                <Badge variant="outline">
                  {assignment.allowLateSubmission ? "Late work accepted" : "No late work"}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/*
            Both are authoring surfaces server-side (GET {id}/audience and the attachment writes stay
            gated on CanAuthorCourseAssignmentsAsync), so a reviewer who cannot edit the brief gets
            neither — the audience card would 403 on mount. The media itself is still shown to them,
            read-only: it is the thing they grade the submission against.
          */}
          {assignment.canManage ? (
            <>
              <AssignmentAudienceCard assignmentId={assignment.id} />

              <BriefMediaManager assignmentId={assignment.id} attachments={assignment.attachments} />
            </>
          ) : (
            <BriefMediaList attachments={assignment.attachments} />
          )}
        </TabsContent>

        {/* ── Submissions ──────────────────────────────────────────────────── */}
        {canReview && (
          <TabsContent value="submissions" className="mt-4">
            {submissions.isLoading ? (
              <TableSkeleton rows={5} />
            ) : submissions.isError ? (
              // Rendered rather than folded into the empty state: a refusal from this endpoint (403
              // "You are not assigned to this course.") is not the same fact as "nobody has submitted",
              // and showing the latter for the former sent us looking for missing rows.
              <p className="text-sm text-destructive">
                {submissions.error instanceof Error
                  ? submissions.error.message
                  : "Could not load the submissions for this assignment."}
              </p>
            ) : rows.length === 0 ? (
              <EmptyState
                icon={Users}
                title="No submissions yet"
                description={
                  assignment.isPublished
                    ? "Students on this course can submit now."
                    : "Publish the assignment so students can start."
                }
              />
            ) : (
              <div className="overflow-x-auto rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Student</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Attempts</TableHead>
                      <TableHead>Returns</TableHead>
                      <TableHead>Files</TableHead>
                      <TableHead>Last submitted</TableHead>
                      <TableHead>Grade</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((submission) => (
                      <TableRow
                        key={submission.id}
                        className="cursor-pointer"
                        onClick={() => setReviewingId(submission.id)}
                      >
                        <TableCell className="font-medium">{submission.studentName}</TableCell>
                        <TableCell>
                          <SubmissionStatusBadge status={submission.status} showIcon={false} />
                        </TableCell>
                        <TableCell>{submission.currentAttemptNumber}</TableCell>
                        <TableCell>
                          {submission.returnedCount > 0 ? (
                            <Badge variant="outline">{submission.returnedCount}×</Badge>
                          ) : (
                            <span className="text-sm text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
                            <Paperclip className="h-3 w-3" aria-hidden />
                            {submission.files.length}
                          </span>
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                          {formatWhen(submission.submittedAt)}
                        </TableCell>
                        <TableCell className="text-sm">
                          {submission.grade !== null
                            ? `${submission.grade} / ${assignment.pointsPossible}`
                            : "—"}
                        </TableCell>
                        <TableCell className="text-right">
                          <ChevronRight
                            className="ml-auto h-4 w-4 text-muted-foreground"
                            aria-hidden
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>
        )}

        {/* ── Not delivered ────────────────────────────────────────────────── */}
        {canReview && (
          <TabsContent value="not-delivered" className="mt-4">
            <NotDeliveredPanel assignmentId={assignment.id} />
          </TabsContent>
        )}

        {/* ── Statistics ───────────────────────────────────────────────────── */}
        <TabsContent value="stats" className="mt-4">
          {stats.isLoading ? (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-24 w-full" />
              ))}
            </div>
          ) : stats.data ? (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <StatTile label="Enrolled students" value={stats.data.totalStudents} />
              <StatTile label="Submitted" value={stats.data.submittedCount} />
              <StatTile label="Not started" value={stats.data.notStartedCount} />
              <StatTile label="Graded" value={stats.data.gradedCount} />
              <StatTile
                label="Awaiting review"
                value={stats.data.pendingReviewCount}
                hint="Waiting on you"
              />
              <StatTile
                label="Awaiting revision"
                value={stats.data.awaitingRevisionCount}
                hint="Waiting on students"
              />
              <StatTile label="Late" value={stats.data.lateCount} />
              <StatTile
                label="Average grade"
                value={
                  stats.data.averageGrade !== null
                    ? `${stats.data.averageGrade.toFixed(1)} / ${assignment.pointsPossible}`
                    : "—"
                }
              />
              <StatTile
                label="Average attempts"
                value={
                  stats.data.averageAttempts !== null ? stats.data.averageAttempts.toFixed(2) : "—"
                }
                hint="How much rework the cohort needed"
              />
              <StatTile label="Total returns" value={stats.data.totalReturns} />
            </div>
          ) : (
            <EmptyState icon={ClipboardList} title="No statistics available" />
          )}
        </TabsContent>
      </Tabs>

      <AssignmentFormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        assignment={assignment}
        courseOptions={[{ value: assignment.courseId, label: assignment.courseName }]}
      />

      <Dialog open={!!reviewingId} onOpenChange={(open) => !open && setReviewingId(null)}>
        <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Review submission</DialogTitle>
            <DialogDescription>
              Grade the latest attempt, or send it back with corrections so the student can revise.
            </DialogDescription>
          </DialogHeader>
          {reviewingId && <SubmissionReviewPanel submissionId={reviewingId} />}
        </DialogContent>
      </Dialog>

      <ShareAssignmentDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        assignmentId={assignment.id}
        assignmentTitle={assignment.title}
        isPublished={assignment.isPublished}
      />

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete &quot;{assignment.title}&quot;?</AlertDialogTitle>
            <AlertDialogDescription>
              It disappears from students&apos; lists immediately. Submitted work is retained rather
              than erased, so an administrator can restore it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                await remove.mutateAsync(assignment.id);
                navigate({ to: "/team-leader/assignments" });
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
